import { IInitialState, RootState } from '../../../reduxService';
import { IMaterialResponse } from '../../api/IApiResponse';
import { convertTo, EConversionSIToImperial } from '../../conversion';
import { MaterialCategory } from '../../enum/Material';
import { UnitOfMeasure } from '../../enum/project';
import { roundNumbers } from '../../utils/utils';
import { IProjectResultResponse } from '../api/IApiResponse';
import { rockD50ExceptionGabion, rockD50ImperialValueGabion, thicknessExceptionGabion, thicknessImperialValueGabion } from '../components/InputForm/Materials/MaterialCategory/Gabion';
import { thicknessException } from '../components/InputForm/Materials/MaterialCategory/Reno';
import { IProjectMacra1 } from '../store/project/projectSlice';

export const selectProject = (state: RootState): IInitialState<IProjectMacra1> => {
    let projectInfo = state.projectInfo.data;
    let project = state.macra1.project.data;
    let isImperial = projectInfo?.unitOfMeasure == UnitOfMeasure.Imperial;

    if (isImperial && project?.hydraulicData && project?.geometry) {
        project = {
            ...project,
            hydraulicData: {
                ...project.hydraulicData,
                discharge: convertTo(project.hydraulicData.discharge, EConversionSIToImperial.m3s_to_ft3s),
                freeboard: convertTo(project.hydraulicData.freeboard, EConversionSIToImperial.m_to_ft),
                radius: convertTo(project.hydraulicData.radius, EConversionSIToImperial.m_to_ft),
                soilAdmissibleVelocity: convertTo(project.hydraulicData.soilAdmissibleVelocity, EConversionSIToImperial.ms_to_fts),
            },
            geometry: project.geometry.map(sec => ({
                ...sec,
                height: convertTo(sec.height, EConversionSIToImperial.m_to_ft),
                width: convertTo(sec.width, EConversionSIToImperial.m_to_ft),
                length: convertTo(sec.length, EConversionSIToImperial.m_to_ft),
                subSectors: sec.subSectors?.map(subSec => ({
                    ...subSec,
                    length: convertTo(subSec.length!, EConversionSIToImperial.m_to_ft),
                })),
            })),
        };
    }

    return {
        ...state.macra1.project,
        data: roundNumbers(project),
    };
};

export const selectProjectWithoutConversion = (state: RootState): IInitialState<IProjectMacra1> => state.macra1.project!;

export const selectMaterialDetail = (state: RootState): IInitialState<IMaterialResponse> => {
    let isImperial = state.projectInfo.data?.unitOfMeasure == UnitOfMeasure.Imperial;
    let materialDetailData = state.materialDetail.data;

    if (isImperial && materialDetailData) {
        materialDetailData = {
            ...materialDetailData,
            physicalData: materialDetailData.physicalData!.map(pd => ({
                ...pd,
                thickness: convertTo(pd.thickness ?? 0, EConversionSIToImperial.m_to_in),
                rockD50: convertTo(pd.rockD50 ?? 0, EConversionSIToImperial.m_to_in),
                allowShearStress: convertTo(pd.allowShearStress ?? 0, EConversionSIToImperial.Nm2_to_lbfft2),
                vegetatedAllowShearStress: convertTo(pd.vegetatedAllowShearStress ?? 0, EConversionSIToImperial.Nm2_to_lbfft2),
            })),
        };
    }

    return {
        ...state.materialDetail,
        data: roundNumbers(materialDetailData),
    };
};

export const selectResults = (state: RootState): IInitialState<IProjectResultResponse> => {
    let isImperial = state.projectInfo.data?.unitOfMeasure == UnitOfMeasure.Imperial;
    let resultsData = state.macra1.results.data;

    if (isImperial && resultsData) {
        resultsData = {
            ...resultsData,
            generalResult: {
                ...resultsData.generalResult,
                general: {
                    ...resultsData.generalResult.general,
                    discharge: convertTo(resultsData.generalResult.general.discharge, EConversionSIToImperial.m3s_to_ft3s),
                    waterDepth: convertTo(resultsData.generalResult.general.waterDepth, EConversionSIToImperial.m_to_ft),
                    avarageWaterVelocity: convertTo(resultsData.generalResult.general.avarageWaterVelocity, EConversionSIToImperial.ms_to_fts),
                    freeboard: convertTo(resultsData.generalResult.general.freeboard, EConversionSIToImperial.m_to_ft),
                    crossSectionalArea: convertTo(resultsData.generalResult.general.crossSectionalArea, EConversionSIToImperial.m2_to_ft2),
                    wettedPerimeter: convertTo(resultsData.generalResult.general.wettedPerimeter, EConversionSIToImperial.m_to_ft),
                    hydraulicRadius: convertTo(resultsData.generalResult.general.hydraulicRadius, EConversionSIToImperial.m_to_ft),
                },
                leftOverbank: {
                    ...resultsData.generalResult.leftOverbank,
                    discharge: convertTo(resultsData.generalResult.leftOverbank.discharge, EConversionSIToImperial.m3s_to_ft3s),
                    avarageWaterVelocity: convertTo(resultsData.generalResult.leftOverbank.avarageWaterVelocity, EConversionSIToImperial.ms_to_fts),
                    crossSectionalArea: convertTo(resultsData.generalResult.leftOverbank.crossSectionalArea, EConversionSIToImperial.m2_to_ft2),
                    wettedPerimeter: convertTo(resultsData.generalResult.leftOverbank.wettedPerimeter, EConversionSIToImperial.m_to_ft),
                    hydraulicRadius: convertTo(resultsData.generalResult.leftOverbank.hydraulicRadius, EConversionSIToImperial.m_to_ft),
                },
                rightOverbank: {
                    ...resultsData.generalResult.rightOverbank,
                    discharge: convertTo(resultsData.generalResult.rightOverbank.discharge, EConversionSIToImperial.m3s_to_ft3s),
                    avarageWaterVelocity: convertTo(resultsData.generalResult.rightOverbank.avarageWaterVelocity, EConversionSIToImperial.ms_to_fts),
                    crossSectionalArea: convertTo(resultsData.generalResult.rightOverbank.crossSectionalArea, EConversionSIToImperial.m2_to_ft2),
                    wettedPerimeter: convertTo(resultsData.generalResult.rightOverbank.wettedPerimeter, EConversionSIToImperial.m_to_ft),
                    hydraulicRadius: convertTo(resultsData.generalResult.rightOverbank.hydraulicRadius, EConversionSIToImperial.m_to_ft),
                },
                mainChannel: {
                    ...resultsData.generalResult.mainChannel,
                    discharge: convertTo(resultsData.generalResult.mainChannel.discharge, EConversionSIToImperial.m3s_to_ft3s),
                    avarageWaterVelocity: convertTo(resultsData.generalResult.mainChannel.avarageWaterVelocity, EConversionSIToImperial.ms_to_fts),
                    crossSectionalArea: convertTo(resultsData.generalResult.mainChannel.crossSectionalArea, EConversionSIToImperial.m2_to_ft2),
                    wettedPerimeter: convertTo(resultsData.generalResult.mainChannel.wettedPerimeter, EConversionSIToImperial.m_to_ft),
                    hydraulicRadius: convertTo(resultsData.generalResult.mainChannel.hydraulicRadius, EConversionSIToImperial.m_to_ft),
                },
            },
            materialsResult: resultsData.materialsResult.map(mat => {
                let thickness = mat.thickness;
                if (thickness === thicknessException) {
                    thickness = Math.ceil(convertTo(thickness, EConversionSIToImperial.m_to_in));
                } else if (mat.category == MaterialCategory.Gabion && thickness == thicknessExceptionGabion) {
                    thickness = thicknessImperialValueGabion;
                } else {
                    thickness = Math.floor(convertTo(thickness, EConversionSIToImperial.m_to_in));
                }
                let rockD50;
                if (mat.category == MaterialCategory.Gabion && mat.rockD50 == rockD50ExceptionGabion) {
                    rockD50 = rockD50ImperialValueGabion;
                } else {
                    rockD50 = Math.ceil(convertTo(mat.rockD50, EConversionSIToImperial.m_to_in) * 10) / 10;
                }
                return {
                    ...mat,
                    thickness: mat.thickness && thickness,
                    rockD50: mat.rockD50 && rockD50,
                    allowedShearStress: mat.allowedShearStress && convertTo(mat.allowedShearStress, EConversionSIToImperial.Nm2_to_lbfft2),
                    calculatedShearStress: mat.calculatedShearStress && convertTo(mat.calculatedShearStress, EConversionSIToImperial.Nm2_to_lbfft2),
                    velocityUnderLining: mat.velocityUnderLining && convertTo(mat.velocityUnderLining, EConversionSIToImperial.ms_to_fts),
                    length: mat.length && convertTo(mat.length, EConversionSIToImperial.m_to_ft),
                };
            }),
            dischargeResult: resultsData.dischargeResult.map(value => ({
                waterDepth: convertTo(value.waterDepth, EConversionSIToImperial.m_to_ft),
                discharge: convertTo(value.discharge, EConversionSIToImperial.m3s_to_ft3s),
            })),
        };
    }
    return {
        ...state.macra1.results,
        data: roundNumbers(resultsData),
    };
};
